import { useEffect, useRef, useState } from 'react'
import { useFrame, useThree } from '@react-three/fiber'
import {
  contadorFpsDaBusca,
  linhasDoContador,
  registrarIntervalo,
  resumirQuadros,
} from './contadorFps'
import type { NivelQualidade } from './qualidadeGrafica'

/** Com que frequência o texto é reescrito: mais que isso só dificulta a leitura. */
const ATUALIZACAO_MS = 500

const ESTILO = [
  'position:fixed',
  'left:56px',
  'bottom:80px',
  'z-index:2147483000',
  'padding:6px 9px',
  'border-radius:6px',
  'background:rgba(8,12,18,0.82)',
  'color:#e6edf3',
  'font:11px/1.45 ui-monospace,SFMono-Regular,Menlo,Consolas,monospace',
  'white-space:pre',
  'pointer-events:none',
].join(';')

/**
 * Mostra FPS, tempo de quadro, chamadas de desenho e triângulos sobre a cena,
 * quando o endereço traz `?fps=1`. Sem o parâmetro não monta nada, não mede e
 * não toca no renderizador.
 *
 * O texto vai direto para um elemento do DOM, e não para o estado do React:
 * atualizar o estado a cada meio segundo redesenharia a árvore em cima da
 * própria medição. As chamadas e triângulos são os do quadro inteiro, somando as
 * passagens de sombra e de pós-processamento, porque o three zera esses
 * contadores a cada `render()` por padrão e só a última passagem sobraria.
 */
export function ContadorFps({ qualidade }: { qualidade: NivelQualidade }) {
  const gl = useThree((estado) => estado.gl)
  const [ativo] = useState(() => typeof window !== 'undefined' && contadorFpsDaBusca(window.location.search))
  const amostras = useRef<number[]>([])
  const ultimoQuadro = useRef<number | null>(null)
  const totalDoQuadro = useRef({ chamadas: 0, triangulos: 0 })
  const nivel = useRef(qualidade)
  nivel.current = qualidade

  // roda antes do `render()` do quadro: guarda o total do quadro anterior e zera para o próximo
  useFrame(() => {
    if (!ativo) return
    totalDoQuadro.current = { chamadas: gl.info.render.calls, triangulos: gl.info.render.triangles }
    gl.info.reset()
    const agora = performance.now()
    if (ultimoQuadro.current !== null) registrarIntervalo(amostras.current, agora - ultimoQuadro.current)
    ultimoQuadro.current = agora
  })

  useEffect(() => {
    if (!ativo) return
    const elemento = document.createElement('div')
    elemento.setAttribute('data-contador-fps', '')
    elemento.style.cssText = ESTILO
    document.body.appendChild(elemento)

    const info = gl.info
    info.autoReset = false
    info.reset()
    // Uma linha espessa sem pontos chega ao three com `instanceCount` infinito. A
    // GPU converte isso em zero instâncias e não desenha nada, mas o contador do
    // three somaria infinito aos triângulos e o total do quadro viraria "∞".
    const atualizarOriginal = info.update
    info.update = (contagem, modo, instancias) =>
      atualizarOriginal.call(info, contagem, modo, Number.isFinite(instancias) ? instancias : 0)

    const atualizar = () => {
      elemento.textContent = linhasDoContador(resumirQuadros(amostras.current), {
        ...totalDoQuadro.current,
        dpr: gl.getPixelRatio(),
        qualidade: nivel.current,
      }).join('\n')
    }
    const intervalo = window.setInterval(atualizar, ATUALIZACAO_MS)
    atualizar()

    return () => {
      window.clearInterval(intervalo)
      info.autoReset = true
      info.update = atualizarOriginal
      elemento.remove()
    }
  }, [ativo, gl])

  return null
}
